import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Quote, Target, Users, Scale, 
  Building2, Heart, Shield, BookOpen, Coins, HandHeart,
  CheckCircle2, Lightbulb, BarChart3, MessageSquare
} from 'lucide-react';

const ManifestoPage = () => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const content = t('manifestoPage');

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const priorityIcons = {
    0: BookOpen,
    1: Coins,
    2: Shield,
    3: Heart,
    4: Users,
    5: Scale,
    6: Users,
    7: Building2
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0510]' : 'bg-[#FAFAF9]'}`}>
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-gradient-to-br from-[#5B2D8E] via-[#7B4AAE] to-[#5B2D8E] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#F7C948] rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#0D9488] rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 lg:px-10 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.span 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block px-6 py-2 bg-[#F7C948]/20 text-[#F7C948] rounded-full text-sm font-bold uppercase tracking-widest mb-6"
            >
              {content.foundingText || 'Texte fondateur'}
            </motion.span>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl lg:text-7xl font-black text-white mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {content.title}
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl lg:text-3xl text-[#F7C948] font-semibold italic"
              style={{ fontFamily: 'var(--font-serif)' }}
            >
              {content.subtitle || 'Une nation, un avenir'}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Préambule - Message du Président */}
      <section className={`py-20 ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="mb-12"
            >
              <span className="text-[#F7C948] font-bold uppercase tracking-widest text-sm">Préambule</span>
              <h2 className={`text-3xl lg:text-4xl font-bold mt-2 ${isDark ? 'text-white' : 'text-[#1C1917]'}`} style={{ fontFamily: 'var(--font-heading)' }}>
                {content.intro?.salutation || 'Aux Ivoiriennes et aux Ivoiriens'}
              </h2>
            </motion.div>

            <div className="space-y-6">
              {[content.intro?.text, content.intro?.text2, content.intro?.text3, content.intro?.text4].filter(Boolean).map((paragraph, index) => (
                <motion.p 
                  key={index}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                  className={`text-lg lg:text-xl leading-relaxed ${isDark ? 'text-stone-300' : 'text-stone-600'}`}
                  style={{ fontFamily: 'var(--font-serif)' }}
                >
                  {paragraph}
                </motion.p>
              ))}
            </div>

            {/* Author Card */}
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className={`mt-12 p-8 rounded-2xl ${isDark ? 'bg-[#5B2D8E]/20 border border-[#5B2D8E]/30' : 'bg-[#5B2D8E]/5 border border-[#5B2D8E]/10'}`}
            >
              <Quote className="w-10 h-10 text-[#F7C948] mb-4" />
              <p className={`text-xl lg:text-2xl font-semibold italic mb-6 ${isDark ? 'text-white' : 'text-[#1C1917]'}`} style={{ fontFamily: 'var(--font-serif)' }}>
                {content.intro?.quote}
              </p>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#5B2D8E] to-[#7B4AAE] flex items-center justify-center text-white font-bold text-xl">
                  AO
                </div>
                <div>
                  <p className={`font-bold text-lg ${isDark ? 'text-white' : 'text-[#1C1917]'}`}>{content.intro?.author}</p>
                  <p className={`text-sm ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>{content.intro?.authorTitle}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sections du Manifeste */}
      {content.sections?.map((section, sectionIndex) => (
        <section 
          key={section.number}
          className={`py-20 ${sectionIndex % 2 === 0 
            ? isDark ? 'bg-[#0A0510]' : 'bg-[#FAFAF9]' 
            : isDark ? 'bg-[#1A0F2E]' : 'bg-white'
          }`}
        >
          <div className="container mx-auto px-4 lg:px-10">
            <div className="max-w-4xl mx-auto">
              {/* Section Header */}
              <motion.div 
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                className="mb-12"
              >
                <div className="flex items-start gap-6">
                  <span 
                    className={`text-7xl lg:text-9xl font-black leading-none ${isDark ? 'text-[#5B2D8E]/30' : 'text-[#5B2D8E]/15'}`}
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {section.number}
                  </span>
                  <div className="pt-4">
                    <h2 className={`text-3xl lg:text-4xl font-bold mb-2 ${isDark ? 'text-white' : 'text-[#1C1917]'}`} style={{ fontFamily: 'var(--font-heading)' }}>
                      {section.title}
                    </h2>
                    {section.subtitle && (
                      <p className="text-[#F7C948] font-semibold text-lg">{section.subtitle}</p>
                    )}
                  </div>
                </div>
                <div className={`h-1 w-24 mt-6 ${isDark ? 'bg-[#F7C948]' : 'bg-[#5B2D8E]'}`} />
              </motion.div>

              {/* Paragraphs */}
              {section.paragraphs && (
                <div className="space-y-6 mb-12">
                  {section.paragraphs.map((paragraph, pIndex) => (
                    <motion.p 
                      key={pIndex}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={fadeIn}
                      className={`text-lg leading-relaxed ${isDark ? 'text-stone-300' : 'text-stone-600'}`}
                      style={{ fontFamily: 'var(--font-serif)' }}
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </div>
              )}

              {/* Principles (for section III) */}
              {section.principles && (
                <div className="grid md:grid-cols-3 gap-6 mt-8">
                  {section.principles.map((principle, pIndex) => (
                    <motion.div 
                      key={pIndex}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={fadeIn}
                      className={`p-6 rounded-xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-[#5B2D8E]/5 border border-[#5B2D8E]/10'}`}
                    >
                      <div className="w-12 h-12 rounded-lg bg-[#5B2D8E] flex items-center justify-center mb-4">
                        {pIndex === 0 && <Scale className="w-6 h-6 text-white" />}
                        {pIndex === 1 && <Users className="w-6 h-6 text-white" />}
                        {pIndex === 2 && <BarChart3 className="w-6 h-6 text-white" />}
                      </div>
                      <h3 className={`font-bold text-lg mb-2 ${isDark ? 'text-white' : 'text-[#1C1917]'}`}>{principle.title}</h3>
                      <p className={`text-sm ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>{principle.text}</p>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Priorities (for section IV) */}
              {section.priorities && (
                <div className="grid md:grid-cols-2 gap-6 mt-8">
                  {section.priorities.map((priority, pIndex) => {
                    const Icon = priorityIcons[pIndex] || Target;
                    return (
                      <motion.div 
                        key={pIndex}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeIn}
                        className={`p-6 rounded-xl ${isDark ? 'bg-white/5 border border-white/10 hover:border-[#F7C948]/30' : 'bg-white border border-stone-200 hover:border-[#5B2D8E]/30'} transition-colors`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#F7C948] to-[#E5A700] flex items-center justify-center flex-shrink-0">
                            <Icon className="w-6 h-6 text-[#1C1917]" />
                          </div>
                          <div>
                            <h3 className={`font-bold text-lg mb-1 ${isDark ? 'text-white' : 'text-[#1C1917]'}`}>{priority.title}</h3>
                            <p className="text-[#0D9488] text-sm font-medium mb-2">{priority.subtitle}</p>
                            <p className={`text-sm ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>{priority.text}</p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* Steps (for section VI - Method) */}
              {section.steps && (
                <div className="grid md:grid-cols-4 gap-4 mt-8">
                  {section.steps.map((step, sIndex) => (
                    <motion.div 
                      key={sIndex}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={fadeIn}
                      className={`p-5 rounded-xl text-center ${isDark ? 'bg-[#5B2D8E]/20' : 'bg-[#5B2D8E]/5'}`}
                    >
                      <span className="text-4xl font-black text-[#F7C948]">{step.number}</span>
                      <h4 className={`font-bold mt-2 mb-2 ${isDark ? 'text-white' : 'text-[#1C1917]'}`}>{step.title}</h4>
                      <p className={`text-xs ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>{step.text}</p>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Values (for section VII) */}
              {section.values && (
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
                  {section.values.map((value, vIndex) => (
                    <motion.div 
                      key={vIndex}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={fadeIn}
                      className={`p-6 rounded-xl border-2 ${isDark ? 'border-[#F7C948]/30 bg-[#F7C948]/5' : 'border-[#F7C948] bg-[#F7C948]/10'}`}
                    >
                      <span className="text-3xl font-black text-[#F7C948]">{value.number}</span>
                      <h4 className={`font-bold mt-2 mb-2 ${isDark ? 'text-white' : 'text-[#1C1917]'}`}>{value.title}</h4>
                      <p className={`text-sm ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>{value.text}</p>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Commitments (for section X) */}
              {section.commitments && (
                <div className="space-y-4 mt-8">
                  {section.commitments.map((commitment, cIndex) => (
                    <motion.div 
                      key={cIndex}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      variants={fadeIn}
                      className={`flex items-start gap-4 p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#0D9488]/5'}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-[#0D9488] flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className={`font-bold ${isDark ? 'text-white' : 'text-[#1C1917]'}`}>
                          {commitment.number}. {commitment.title}
                        </h4>
                        <p className={`text-sm mt-1 ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>{commitment.text}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      ))}

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-[#5B2D8E] via-[#7B4AAE] to-[#5B2D8E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#F7C948] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#0D9488] rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 lg:px-10 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <span className="text-[#F7C948] font-bold uppercase tracking-widest text-sm">{content.callToAction?.title || 'Notre appel'}</span>
              <h2 className="text-4xl lg:text-5xl font-black text-white mt-4 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
                {content.callToAction?.subtitle || 'Rejoignez le mouvement'}
              </h2>
            </motion.div>

            <motion.p 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="text-xl text-white/80 mb-6"
              style={{ fontFamily: 'var(--font-serif)' }}
            >
              {content.callToAction?.text}
            </motion.p>

            <motion.p 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="text-lg text-white/70 mb-8"
              style={{ fontFamily: 'var(--font-serif)' }}
            >
              {content.callToAction?.appeal}
            </motion.p>

            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="mb-8"
            >
              <p className="text-2xl lg:text-3xl font-bold text-[#F7C948] mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                {content.callToAction?.closing}
              </p>
              <p className="text-3xl lg:text-4xl font-black text-white" style={{ fontFamily: 'var(--font-heading)' }}>
                {content.callToAction?.motto}
              </p>
            </motion.div>

            <motion.div 
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link 
                to="/rejoindre"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#F7C948] text-[#1C1917] font-bold rounded-lg hover:bg-[#E5A700] transition-colors"
              >
                Rejoindre le mouvement
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/constitution"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-bold rounded-lg hover:bg-white/20 transition-colors border border-white/20"
              >
                Découvrir la 4ᵉ République
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className={`py-12 ${isDark ? 'bg-[#1A0F2E]' : 'bg-[#F5F0E8]'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6 max-w-4xl mx-auto">
            <Link 
              to="/" 
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${isDark ? 'text-[#F7C948] hover:bg-white/10' : 'text-[#5B2D8E] hover:bg-stone-100'}`}
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à l&apos;accueil
            </Link>
            <Link 
              to="/organigramme" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#5B2D8E] text-white font-semibold rounded-lg hover:bg-[#4A2473] transition-colors"
            >
              Notre organisation
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ManifestoPage;
